/**
 * ========================================
 * API SERVİSLERİ
 * ========================================
 * Backend API'leri ile iletişim için merkezi servis dosyası
 * Axios interceptor'ları ile token yönetimi ve hata kontrolü
 */

// ==================== DEPENDENCIES ====================
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// ==================== CONFIGURATION ====================
// API Base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// CSRF Token storage
let csrfToken: string | null = null;

// Axios instance oluştur
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Cookie'leri gönder
});

// ==================== CSRF TOKEN FETCHER ====================
/**
 * CSRF token'ı backend'den alır ve saklar
 */
const fetchCsrfToken = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_URL}/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.csrfToken;
    return csrfToken || '';
  } catch (error) {
    console.error('CSRF token alma hatası:', error);
    throw error;
  }
};

// ==================== INTERCEPTORS(ÖNLEYİCİLER) ====================
// Request interceptor - Her istekte token ve CSRF ekle
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // JWT Token ekle
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // CSRF Token ekle (POST, PUT, DELETE için)
    if (config.method && ['post', 'put', 'delete'].includes(config.method.toLowerCase())) {
      // CSRF token yoksa al
      if (!csrfToken) {
        try {
          await fetchCsrfToken();
        } catch (error) {
          console.error('CSRF token alınamadı');
        }
      }

      // CSRF token'ı header'a ekle
      if (csrfToken && config.headers) {
        config.headers['CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Hata yönetimi ve CSRF token yenileme
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401 - Unauthorized (Token geçersiz)
    if (error.response?.status === 401) {
      // Eğer login endpoint'inden 401 geliyorsa (yanlış şifre), yönlendirme yapma
      const isLoginRequest = error.config?.url?.includes('/auth/login');

      if (!isLoginRequest) {
        // Sadece token geçersizse (başka endpoint'lerden 401) yönlendir
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // Login hatalarını olduğu gibi fırlat, sayfa yenileme
    }

    // 403 - CSRF token hatası
    if (error.response?.status === 403 && error.response?.data?.code === 'INVALID_CSRF_TOKEN') {
      // CSRF token'ı yeniden al
      csrfToken = null;
      try {
        await fetchCsrfToken();
        // İsteği tekrar dene
        const originalRequest = error.config;
        if (csrfToken && originalRequest.headers) {
          originalRequest.headers['CSRF-Token'] = csrfToken;
          return api(originalRequest);
        }
      } catch (retryError) {
        console.error('CSRF token yenileme hatası:', retryError);
      }
    }

    return Promise.reject(error);
  }
);

// ==================== AUTH API ====================
export const authAPI = {
  // Kullanıcı kaydı
  register: (data: {
    name: string;
    surname: string;
    email: string;
    password: string;
  }) => api.post('/auth/register', data),

  // Kullanıcı girişi
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  // Kullanıcı çıkışı
  logout: () => api.post('/auth/logout'),

  // Kullanıcı profili
  getProfile: () => api.get('/auth/profile'),

  // Profil güncelleme
  updateProfile: (data: { name: string; email: string }) =>
    api.put('/auth/profile', data),

  // Şifre değiştirme
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/auth/password', { currentPassword, newPassword }),

  // Hesap silme
  deleteAccount: () => api.delete('/auth/account'),

  // Email doğrulama
  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),

  // Doğrulama kodunu yeniden gönder
  resendVerificationCode: (email: string) =>
    api.post('/auth/resend-code', { email }),

  // Email değiştirme talebi (eski maile kod gönder)
  requestEmailChange: (newEmail: string) =>
    api.post('/auth/request-email-change', { newEmail }),

  // Email değiştirme doğrulama
  verifyEmailChange: (code: string, newEmail: string) =>
    api.post('/auth/verify-email-change', { code, newEmail }),
};

// ==================== ANNOUNCEMENTS API ====================
export const announcementAPI = {
  // Tüm duyuruları getir (public)
  getAll: () => api.get('/announcements'),

  // Slug ile duyuru getir
  getBySlug: (slug: string) => api.get(`/announcements/${slug}`),

  // Yeni duyuru oluştur
  create: (data: { title: string; content: string; is_published?: boolean }) =>
    api.post('/announcements', data),

  // Duyuru güncelle
  update: (
    id: number,
    data: { title?: string; content?: string; is_published?: boolean }
  ) => api.put(`/announcements/${id}`, data),

  // Duyuru sil
  delete: (id: number) => api.delete(`/announcements/${id}`),

  // Tüm duyuruları getir (admin)
  getAllAdmin: () => api.get('/announcements/admin/all'),
};

// ==================== GALLERY API ====================
export const galleryAPI = {
  // Tüm galeri resimlerini getir (public)
  getAll: () => api.get('/gallery'),

  // Resim yükle
  upload: (formData: FormData) =>
    api.post('/gallery', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Resim güncelle
  update: (
    id: number,
    data: { title?: string; description?: string; is_active?: boolean }
  ) => api.put(`/gallery/${id}`, data),

  // Resim sil
  delete: (id: number) => api.delete(`/gallery/${id}`),

  // Tüm resimleri getir (admin)
  getAllAdmin: () => api.get('/gallery/admin/all'),
};

// ==================== FAQ API ====================
export const faqAPI = {
  // Tüm SSS'leri getir (public)
  getAll: (category?: string) =>
    api.get('/faqs', category ? { params: { category } } : {}),

  // Kategorileri getir
  getCategories: () => api.get('/faqs/categories'),

  // Yeni SSS oluştur
  create: (data: {
    question: string;
    answer: string;
    category?: string;
    display_order?: number;
  }) => api.post('/faqs', data),

  // SSS güncelle
  update: (
    id: number,
    data: {
      question?: string;
      answer?: string;
      category?: string;
      display_order?: number;
      is_active?: boolean;
    }
  ) => api.put(`/faqs/${id}`, data),

  // SSS sil
  delete: (id: number) => api.delete(`/faqs/${id}`),

  // Tüm SSS'leri getir (admin)
  getAllAdmin: () => api.get('/faqs/admin/all'),
};

// ==================== STATS API ====================
export const statsAPI = {
  // Genel istatistikler (public)
  getStats: () => api.get('/stats'),

  // Detaylı istatistikler (admin)
  getDetailedStats: () => api.get('/stats/detailed'),
};

// ==================== USERS API ====================
export const usersAPI = {
  // Tüm kullanıcıları getir
  getAll: () => api.get('/users'),

  // Tek kullanıcı getir
  getUser: (id: number) => api.get(`/users/${id}`),

  // Kullanıcı rolünü güncelle
  updateRole: (id: number, role_id: number) =>
    api.put(`/users/${id}/role`, { role_id }),

  // Kullanıcı aktif/pasif durumunu değiştir
  toggleActive: (id: number) => api.put(`/users/${id}/toggle-active`),

  // Kullanıcı sil
  delete: (id: number) => api.delete(`/users/${id}`),
};

// ==================== EXPORT ====================
export default api;
